import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '@/store/authStore';
import { authAPI } from '@/services/api';
import { toast } from '@/components/ui/use-toast';

/**
 * Hook pour déconnecter automatiquement l'utilisateur après une période d'inactivité
 * @param {number} timeoutMinutes - Nombre de minutes d'inactivité avant déconnexion (défaut: 5)
 */
const useInactivityLogout = (timeoutMinutes = 5) => {
  const { isAuthenticated, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Convertir les minutes en millisecondes
  const timeoutMs = timeoutMinutes * 60 * 1000;

  // Pages publiques où la déconnexion automatique ne doit pas s'appliquer
  const publicPaths = ['/auth', '/'];
  const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

  // Fonction de déconnexion
  const handleLogout = useCallback(async () => {
    try {
      // Appeler l'API de déconnexion backend pour détruire la session
      await authAPI.logout();
    } catch (error) {
      console.log('Erreur lors de la déconnexion automatique:', error);
    } finally {
      // Nettoyer le store frontend
      logout();
      toast({
        title: 'Session expirée',
        description: 'Vous avez été déconnecté automatiquement.',
        variant: 'default',
      });
      navigate('/auth?tab=login');
    }
  }, [logout, navigate]);

  // Réinitialiser le timer d'inactivité
  const resetTimer = useCallback(() => {
    // Ne pas réinitialiser si l'utilisateur n'est pas authentifié ou sur une page publique
    if (!isAuthenticated || isPublicPath) {
      return;
    }

    // Mettre à jour la dernière activité
    lastActivityRef.current = Date.now();

    // Effacer le timer existant
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Créer un nouveau timer
    timeoutRef.current = setTimeout(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;

      // Vérifier que l'inactivité est toujours valide (au cas où l'utilisateur serait revenu)
      if (timeSinceLastActivity >= timeoutMs && isAuthenticated && !isPublicPath) {
        handleLogout();
      }
    }, timeoutMs);
  }, [isAuthenticated, isPublicPath, timeoutMs, handleLogout]);

  // Événements à écouter pour détecter l'activité
  useEffect(() => {
    if (!isAuthenticated || isPublicPath) {
      // Nettoyer le timer si l'utilisateur n'est pas authentifié ou sur une page publique
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Liste des événements qui indiquent une activité utilisateur
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    // Ajouter les écouteurs d'événements
    events.forEach((event) => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Initialiser le timer
    resetTimer();

    // Nettoyage
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, isPublicPath, resetTimer]);

  // Réinitialiser le timer lors du changement de route (si authentifié)
  useEffect(() => {
    if (isAuthenticated && !isPublicPath) {
      resetTimer();
    }
  }, [location.pathname, isAuthenticated, isPublicPath, resetTimer]);

  // Gérer la visibilité de la page (onglet actif/inactif)
  useEffect(() => {
    if (!isAuthenticated || isPublicPath) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page cachée : ne pas réinitialiser le timer, mais continuer à compter
        // L'inactivité continue même si l'onglet n'est pas visible
      } else {
        // Page visible : vérifier si l'inactivité a dépassé le timeout
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity >= timeoutMs) {
          handleLogout();
        } else {
          // Réinitialiser le timer si la page redevient visible avant le timeout
          resetTimer();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isPublicPath, timeoutMs, handleLogout, resetTimer]);

  // Vérifier périodiquement l'inactivité (toutes les 30 secondes)
  // Cela permet de gérer les cas où les événements ne sont pas capturés
  useEffect(() => {
    if (!isAuthenticated || isPublicPath) {
      return;
    }

    const checkInterval = setInterval(() => {
      // Ne vérifier que si la page est visible
      if (!document.hidden) {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;

        if (timeSinceLastActivity >= timeoutMs) {
          handleLogout();
        }
      }
    }, 30000); // Vérifier toutes les 30 secondes

    return () => {
      clearInterval(checkInterval);
    };
  }, [isAuthenticated, isPublicPath, timeoutMs, handleLogout]);
};

export default useInactivityLogout;
